import React, { Fragment } from 'react';

import { Desktop } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import { TFunction } from 'i18next';
import { CostCenter } from 'models/costCenter';
import { CostCenterReportRequest, CostCenterReportResponse } from 'models/costCenterReport';

import CostCenterReportForm from './CostCenterReportFilter';
import { Container, Divider, Table, Title } from './styles';

export const RenderTravelReportViewCostCenter = (
  showViewInnerFilter: boolean,
  costCenterList: CostCenter[],
  isLoading: boolean,
  isSubmit: boolean,
  isMobile: boolean,
  t: TFunction,
  fetchReportSummary: (filters: CostCenterReportRequest, type: string) => Promise<any>,
  handlePeriodText: (text?: string) => void,
  formattedPrice: (price?: number, currency?: string) => string,
  formatDate: (date: string) => string,
  formatStatus: (status: string) => string,
  report?: CostCenterReportResponse,
) => (
  <Container>
    {showViewInnerFilter && (
      <Fragment>
        <Desktop>
          <ScreenHeader title={t('costCenter.report.title')} subtitle={t('costCenter.report.subtitle')} />
        </Desktop>
        <CostCenterReportForm
          loading={isLoading}
          costCenterList={costCenterList}
          fetchReportData={fetchReportSummary}
          onSetPeriodText={handlePeriodText}
          isSubmit={isSubmit}
        />
        <Divider />
        <br />
      </Fragment>
    )}

    {!isLoading && report && !isMobile && (
      <Container>
        {/* Flights */}
        <Title>{t('booking.flights')}</Title>
        <Table>
          <table>
            <tr>
              <Table.Item className="title">#</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.travelArranger')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.traveler')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.costCenter')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.group')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.fare')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.tax')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.dateEmission')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.status')}</Table.Item>
              <Table.Item className="title">PNR</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.airCompany')}</Table.Item>
            </tr>
            {report?.air.map((booking) => (
              <>
                <tr key={booking.travelItinerary}>
                  <Table.Item key={booking.travelItinerary}>{booking.travelItinerary}</Table.Item>
                  <Table.Item key={booking.user}>{booking.user}</Table.Item>
                  <Table.Item key={booking.travelerName}>{booking.travelerName}</Table.Item>
                  <Table.Item key={booking.costCenter}>{booking.costCenter}</Table.Item>
                  <Table.Item key={booking.group}>{booking.group}</Table.Item>
                  <Table.Item key={booking.amount}>{formattedPrice(booking.amount, booking.currency)}</Table.Item>
                  <Table.Item key={booking.taxes}>{formattedPrice(booking.taxes, booking.currency)}</Table.Item>
                  <Table.Item key={booking.creationDate}>{formatDate(booking.creationDate)}</Table.Item>
                  <Table.Item key={booking.travelStatus}>{formatStatus(booking.travelStatus)}</Table.Item>
                  <Table.Item key={booking.pnrCode}>{booking.pnrCode !== '' ? booking.pnrCode : '-'}</Table.Item>
                  <Table.Item key={booking.airline}>
                    {booking.airline} ({booking.airlineCode})
                  </Table.Item>
                </tr>
              </>
            ))}
          </table>
        </Table>
        {/* Hotels */}
        <Title>{t('booking.hotels')}</Title>
        <Table>
          <table>
            <tr>
              <Table.Item className="title">#</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.travelArranger')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.traveler')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.costCenter')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.group')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.fare')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.tax')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.dateEmission')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.status')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.destination')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.hotelName')}</Table.Item>
            </tr>
            {report?.hotel.map((booking) => (
              <>
                <tr key={booking.travelItinerary}>
                  <Table.Item key={booking.travelItinerary}>{booking.travelItinerary}</Table.Item>
                  <Table.Item key={booking.user}>{booking.user}</Table.Item>
                  <Table.Item key={booking.travelerName}>{booking.travelerName}</Table.Item>
                  <Table.Item key={booking.costCenter}>{booking.costCenter}</Table.Item>
                  <Table.Item key={booking.group}>{booking.group}</Table.Item>
                  <Table.Item key={booking.amount}>{formattedPrice(booking.amount, booking.currency)}</Table.Item>
                  <Table.Item key={booking.taxes}>{formattedPrice(booking.taxes, booking.currency)}</Table.Item>
                  <Table.Item key={booking.creationDate}>{formatDate(booking.creationDate)}</Table.Item>
                  <Table.Item key={booking.travelStatus}>{formatStatus(booking.travelStatus)}</Table.Item>
                  <Table.Item key={booking.destination}>{booking.destination}</Table.Item>
                  <Table.Item key={booking.hotelName}>{booking.hotelName}</Table.Item>
                </tr>
              </>
            ))}
          </table>
        </Table>
        {/* Vehicles */}
        <Title>{t('booking.autos')}</Title>
        <Table>
          <table>
            <tr>
              <Table.Item className="title">#</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.travelArranger')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.traveler')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.costCenter')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.group')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.fare')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.tax')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.dateEmission')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.status')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.pickUp')}</Table.Item>
              <Table.Item className="title">{t('costCenter.report.columns.dropOff')}</Table.Item>
            </tr>
            {report?.vehicle.map((booking) => (
              <>
                <tr key={booking.travelItinerary}>
                  <Table.Item key={booking.travelItinerary}>{booking.travelItinerary}</Table.Item>
                  <Table.Item key={booking.user}>{booking.user ?? booking.travelerArrangerName}</Table.Item>
                  <Table.Item key={booking.travelerName}>{booking.travelerName}</Table.Item>
                  <Table.Item key={booking.costCenter}>{booking.costCenter}</Table.Item>
                  <Table.Item key={booking.group}>{booking.group}</Table.Item>
                  <Table.Item key={booking.amount}>{formattedPrice(booking.amount, booking.currency)}</Table.Item>
                  <Table.Item key={booking.taxes}>{formattedPrice(booking.taxes, booking.currency)}</Table.Item>
                  <Table.Item key={booking.creationDate}>{formatDate(booking.creationDate)}</Table.Item>
                  <Table.Item key={booking.travelStatus}>{formatStatus(booking.travelStatus)}</Table.Item>
                  <Table.Item key={booking.pickupLocation}>{booking.pickupLocation}</Table.Item>
                  <Table.Item key={booking.dropoffLocation}>{booking.dropoffLocation}</Table.Item>
                </tr>
              </>
            ))}
          </table>
        </Table>
      </Container>
    )}
  </Container>
);
